// src/components/admin/ReportCard.jsx
// Chart / table card with an Excel export, used by the Reports AI assistant (Reports Management > Reports AI).
import React, { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import '../../styles/reports.css';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer
} from 'recharts';

const CHART_COLORS = ['#E64C31', '#f7931e', '#00d4aa', '#3498db', '#9b59b6', '#ff6b35', '#2ecc71', '#e74c3c'];

// ─────────────────────────────────────────────
// ReportCard: renders chart + table + export
// ─────────────────────────────────────────────
function ReportCard({ reportData }) {
  const [viewMode, setViewMode] = useState('chart'); // 'chart' | 'table'
  const { chartType, title, description, xAxisKey, dataKeys = [], data = [], columns = [] } = reportData;

  const exportToExcel = useCallback(() => {
    const rows = data.map(row => {
      const result = {};
      columns.forEach(col => {
        result[col.label] = row[col.key];
      });
      return result;
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, title.slice(0, 31));
    XLSX.writeFile(wb, `${title}.xlsx`);
  }, [data, columns, title]);

  const renderChart = () => {
    if (!data || data.length === 0) {
      return <p className="no-chart-data">No data available for this report.</p>;
    }

    const commonProps = {
      data,
      margin: { top: 5, right: 20, left: 10, bottom: 60 }
    };

    switch (chartType) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey={xAxisKey} tick={{ fontSize: 11 }} angle={-35} textAnchor="end" interval={0} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ paddingTop: 16 }} />
              {dataKeys.map((dk, i) => (
                <Bar key={dk.key} dataKey={dk.key} name={dk.label} fill={dk.color || CHART_COLORS[i % CHART_COLORS.length]} radius={[3, 3, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey={xAxisKey} tick={{ fontSize: 11 }} angle={-35} textAnchor="end" interval={0} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ paddingTop: 16 }} />
              {dataKeys.map((dk, i) => (
                <Line key={dk.key} type="monotone" dataKey={dk.key} name={dk.label} stroke={dk.color || CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart {...commonProps}>
              <defs>
                {dataKeys.map((dk, i) => (
                  <linearGradient key={dk.key} id={`grad-${dk.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={dk.color || CHART_COLORS[i % CHART_COLORS.length]} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={dk.color || CHART_COLORS[i % CHART_COLORS.length]} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey={xAxisKey} tick={{ fontSize: 11 }} angle={-35} textAnchor="end" interval={0} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ paddingTop: 16 }} />
              {dataKeys.map((dk, i) => (
                <Area key={dk.key} type="monotone" dataKey={dk.key} name={dk.label} stroke={dk.color || CHART_COLORS[i % CHART_COLORS.length]} fill={`url(#grad-${dk.key})`} strokeWidth={2} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        );

      case 'pie': {
        const valueKey = dataKeys[0]?.key || 'value';
        return (
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie data={data} nameKey={xAxisKey} dataKey={valueKey} cx="50%" cy="45%" outerRadius={110} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                {data.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(val) => val.toLocaleString()} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );
      }

      default:
        return null;
    }
  };

  const showChartToggle = chartType !== 'table' && data.length > 0;

  return (
    <div className="report-card">
      <div className="report-card-header">
        <div className="report-card-title-group">
          <h4 className="report-card-title">{title}</h4>
          {description && <p className="report-card-description">{description}</p>}
        </div>
        <div className="report-card-actions">
          {showChartToggle && (
            <div className="report-view-toggle">
              <button
                className={`view-toggle-btn ${viewMode === 'chart' ? 'active' : ''}`}
                onClick={() => setViewMode('chart')}
              >
                Chart
              </button>
              <button
                className={`view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
              >
                Table
              </button>
            </div>
          )}
          <button className="report-export-btn" onClick={exportToExcel} title="Export to Excel">
            ⬇ Excel
          </button>
        </div>
      </div>

      <div className="report-card-body">
        {(viewMode === 'chart' && chartType !== 'table') ? (
          <div className="report-chart-area">
            {renderChart()}
          </div>
        ) : (
          <div className="report-table-scroll">
            <table className="report-data-table">
              <thead>
                <tr>
                  {columns.map(col => (
                    <th key={col.key}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={i}>
                    {columns.map(col => (
                      <td key={col.key}>
                        {typeof row[col.key] === 'number'
                          ? row[col.key].toLocaleString(undefined, { maximumFractionDigits: 2 })
                          : (row[col.key] ?? '—')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="report-card-footer">
        {data.length} record{data.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}

export default ReportCard;
